import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { 
  TextField, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Box 
} from '@mui/material';
import { 
  Container, 
  Header, 
  HeaderLeft, 
  Title, 
  Content, 
  BackButton, 
  HeaderButton,
  TabContainer,
  TabButton,
  FormContainer,
  FormSection,
  SectionTitle,
  FormRow
} from '../../EmployeesRepository/EmployeeForm/styles';
import LoadingSpinner from '../../common/LoadingSpinner';
import EditableItemList from '../../MyActivities/MyCV/components/common/EditableItemList';
import RichTextEditor from '../../common/RichTextEditor';
import ConfirmationModal from '../../common/ConfirmationModal';
import { useAlert } from '../../../contexts/AlertContext';
import { skillsApi } from '../../../services/api';
import { 
  SKILL_TYPES, 
  getStatusText 
} from '../../../constants/skillTypes';

const TABS = {
  SKILL_DATA: 'SKILL_DATA',
  FOLLOW_UP_QUESTIONS: 'FOLLOW_UP_QUESTIONS'
};

const SkillsItem = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(id ? true : false);
  const [activeTab, setActiveTab] = useState(TABS.SKILL_DATA);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    description: ''
  });
  const [followUpQuestions, setFollowUpQuestions] = useState([]);
  const [expandedQuestionIndex, setExpandedQuestionIndex] = useState(null);
  const [deleteQuestionModal, setDeleteQuestionModal] = useState({ open: false, questionIndex: null, question: null });
  const [initialQuestions, setInitialQuestions] = useState([]);
  const [isQuestionsLoading, setIsQuestionsLoading] = useState(false);

  const fetchSkill = async () => {
    try {
      setIsLoading(true);
      const skill = await skillsApi.getSkillById(id);
      setFormData({
        name: skill.name,
        type: skill.type,
        description: skill.description || ''
      });
      // Set follow-up questions from API response
      const questions = skill.followUpQuestions || [];
      setFollowUpQuestions(questions);
      setInitialQuestions(JSON.parse(JSON.stringify(questions))); // Deep copy for comparison
    } catch (err) {
      showAlert('Failed to load skill.', 'error', err);
      console.error('Error fetching skill:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchSkill();
    }
  }, [id]);

  // Watch for changes in followUpQuestions and handle API calls
  useEffect(() => {
    if (!id || initialQuestions.length === 0 && followUpQuestions.length === 0) return;

    const handleApiUpdates = async () => {
      setIsQuestionsLoading(true);
      try {
        // Handle new questions
        for (const question of followUpQuestions) {
          if (question.isNew && question.question.trim()) {
            const questionData = {
              question: question.question,
              instructions: question.instructions,
              order: question.order
            };
            
            const savedQuestion = await skillsApi.createFollowUpQuestion(id, questionData);
            
            // Update the question in the array to remove isNew flag and add real ID
            setFollowUpQuestions(prev => prev.map(q => 
              q === question ? { ...savedQuestion, isNew: false } : q
            ));
            
            showAlert('Question created successfully', 'success');
          }
        }

        // Update order for all questions based on their current position
        const updatedQuestions = followUpQuestions.map((q, index) => ({
          ...q,
          order: index + 1
        }));

        // Check if any orders have changed and update them
        const orderUpdatePromises = [];
        for (let i = 0; i < updatedQuestions.length; i++) {
          const currentQuestion = updatedQuestions[i];
          const originalQuestion = initialQuestions.find(q => q.id === currentQuestion.id);
          
          if (originalQuestion && !currentQuestion.isNew && currentQuestion.id) {
            // Check if content changed
            if (originalQuestion.question !== currentQuestion.question || 
                originalQuestion.instructions !== currentQuestion.instructions) {
              const questionData = {
                question: currentQuestion.question,
                instructions: currentQuestion.instructions
              };
              
              await skillsApi.updateFollowUpQuestion(currentQuestion.id, questionData);
              showAlert('Question updated successfully', 'success');
            }
            
            // Check if order changed
            if (originalQuestion.order !== currentQuestion.order) {
              orderUpdatePromises.push(
                skillsApi.updateFollowUpQuestionOrder(currentQuestion.id, currentQuestion.order)
              );
            }
          }
        }

        // Execute all order updates in parallel
        if (orderUpdatePromises.length > 0) {
          await Promise.all(orderUpdatePromises);
          showAlert('Question order updated successfully', 'success');
        }

        // Update followUpQuestions with new orders if they changed
        if (followUpQuestions.some((q, index) => q.order !== index + 1)) {
          setFollowUpQuestions(updatedQuestions);
        }

        // Update initial questions to current state for next comparison
        setInitialQuestions(JSON.parse(JSON.stringify(updatedQuestions.filter(q => !q.isNew))));
      } catch (err) {
        showAlert(err.message || 'Failed to save changes. Please try again.', 'error', err);
        console.error('Error saving questions:', err);
      } finally {
        setIsQuestionsLoading(false);
      }
    };

    const timeoutId = setTimeout(handleApiUpdates, 1000); // Debounce API calls
    return () => clearTimeout(timeoutId);
  }, [followUpQuestions, id, initialQuestions]);

  const handleInputChange = (name) => (event) => {
    const { value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      if (id) {
        await skillsApi.updateSkill(id, formData);
        showAlert('Skill updated successfully', 'success');
      } else {
        const newSkill = await skillsApi.createSkill(formData);
        showAlert('Skill created successfully', 'success');
        navigate(`/skills-matrix/skills/${newSkill.id}`, { replace: true });
      }
    } catch (err) {
      showAlert(err.message || `Failed to ${id ? 'update' : 'create'} skill. Please try again.`, 'error', err);
      console.error(`Error ${id ? 'updating' : 'creating'} skill:`, err);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewQuestion = () => ({
    id: `temp-${Date.now()}`,
    question: '',
    instructions: '',
    order: followUpQuestions.length + 1,
    isNew: true
  });

  const handleDeleteQuestion = (questionIndex) => {
    const question = followUpQuestions[questionIndex];
    setDeleteQuestionModal({ 
      open: true, 
      questionIndex, 
      question 
    });
  };

  const confirmDeleteQuestion = async () => {
    const { questionIndex, question } = deleteQuestionModal;
    
    try {
      setIsQuestionsLoading(true);
      // Only call API if the question exists on the server (has a real ID)
      if (question.id && !question.isNew) {
        await skillsApi.deleteFollowUpQuestion(question.id);
      }
      
      const newQuestions = [...followUpQuestions];
      newQuestions.splice(questionIndex, 1);
      
      // Update order for remaining questions
      const updatedQuestions = newQuestions.map((q, index) => ({
        ...q,
        order: index + 1
      }));
      
      setFollowUpQuestions(updatedQuestions);
      
      // Adjust expanded index if necessary
      if (expandedQuestionIndex === questionIndex) {
        setExpandedQuestionIndex(null);
      } else if (expandedQuestionIndex > questionIndex) {
        setExpandedQuestionIndex(expandedQuestionIndex - 1);
      }
      
      setDeleteQuestionModal({ open: false, questionIndex: null, question: null });
      showAlert('Question deleted successfully', 'success');
    } catch (err) {
      showAlert(err.message || 'Failed to delete question. Please try again.', 'error', err);
      console.error('Error deleting question:', err);
    } finally {
      setIsQuestionsLoading(false);
    }
  };

  const renderQuestionSummary = (question) => (
    <Box>
      <Box sx={{ fontWeight: 500, color: '#1f2937' }}>
        {question.question || 'Untitled Question'}
      </Box>
      <Box sx={{ fontSize: '14px', color: '#6b7280', mt: 0.5 }}>
        {question.instructions && question.instructions.trim() ? 
          'Instructions attached' : 
          'No instructions'
        }
      </Box>
    </Box>
  );

  const renderQuestionEditForm = (question, onChange) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Question"
        name="question"
        required
        fullWidth
        variant="outlined"
        autoComplete="off"
        value={question.question}
        onChange={(e) => onChange({ ...question, question: e.target.value })}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: '56px'
          }
        }}
      />
      
      <Box>
        <RichTextEditor
          initialContent={question.instructions}
          description="Add instructions or additional context for this question"
          onChange={(content) => onChange({ ...question, instructions: content })}
        />
      </Box>
    </Box>
  );

  const renderSkillData = () => (
    <FormContainer>
      <FormSection>
        <SectionTitle>Basic Information</SectionTitle>
        <FormRow>
          <TextField
            label="Name"
            name="name"
            required
            fullWidth
            variant="outlined"
            autoComplete="off"
            value={formData.name}
            onChange={handleInputChange('name')}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '56px'
              }
            }}
          />
        </FormRow>
        
        <FormRow>
          <FormControl fullWidth required>
            <InputLabel>Type</InputLabel>
            <Select
              value={formData.type}
              onChange={handleInputChange('type')}
              label="Type"
            >
              <MenuItem value={SKILL_TYPES.TECHNICAL}>{getStatusText(SKILL_TYPES.TECHNICAL)}</MenuItem>
              <MenuItem value={SKILL_TYPES.SOFT}>{getStatusText(SKILL_TYPES.SOFT)}</MenuItem>
            </Select>
          </FormControl>
        </FormRow>

        <FormRow>
          <TextField
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            autoComplete="off"
            value={formData.description}
            onChange={handleInputChange('description')}
          />
        </FormRow>
      </FormSection>
    </FormContainer>
  );

  const renderFollowUpQuestions = () => (
    <FormContainer>
      <FormSection>
        <SectionTitle>Follow-up Questions</SectionTitle>
        <EditableItemList
          items={followUpQuestions}
          setItems={setFollowUpQuestions}
          expandedIndex={expandedQuestionIndex}
          setExpandedIndex={setExpandedQuestionIndex}
          createNewItem={createNewQuestion}
          renderSummary={renderQuestionSummary}
          renderEditForm={renderQuestionEditForm}
          addButtonText="Add Question"
          emptyState={{
            title: "No follow-up questions",
            description: "Add questions to help guide skill assessment and development."
          }}
          itemName="question"
          isLoading={isQuestionsLoading}
          onDelete={handleDeleteQuestion}
        />
      </FormSection>
    </FormContainer>
  );

  const renderContent = () => {
    switch (activeTab) {
      case TABS.SKILL_DATA:
        return renderSkillData();
      case TABS.FOLLOW_UP_QUESTIONS:
        return renderFollowUpQuestions();
      default:
        return null;
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/skills-matrix/skills')}>
            <FaArrowLeft /> Back to Skills
          </BackButton>
          <Title>
            {id ? `Edit Skill | ${formData.name}` : 'New Skill'}
          </Title>
        </HeaderLeft>
        {activeTab === TABS.SKILL_DATA && (
          <HeaderButton onClick={handleSubmit} disabled={!formData.name.trim() || !formData.type}>
            {id ? 'Update Skill' : 'Create Skill'}
          </HeaderButton>
        )}
      </Header>

      <TabContainer>
        <TabButton
          data-active={activeTab === TABS.SKILL_DATA}
          onClick={() => setActiveTab(TABS.SKILL_DATA)}
        >
          Skill Data
        </TabButton>
        {id && (
          <TabButton
            data-active={activeTab === TABS.FOLLOW_UP_QUESTIONS}
            onClick={() => setActiveTab(TABS.FOLLOW_UP_QUESTIONS)}
          >
            Follow-up Questions
          </TabButton>
        )}
      </TabContainer>

      <Content>
        {renderContent()}
      </Content>

      {/* Delete Question Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteQuestionModal.open}
        onClose={() => setDeleteQuestionModal({ open: false, questionIndex: null, question: null })}
        onConfirm={confirmDeleteQuestion}
        title="Delete Question"
        message={`Are you sure you want to delete the question "${deleteQuestionModal.question?.question || 'Untitled Question'}"? This action cannot be undone.`}
      />
    </Container>
  );
};

export default SkillsItem; 